import { Stripe } from "@stripe/stripe-js";
import { EventEmitter } from "@angular/core";
import { IBookingDefaults } from "./main/settings/settings.service";

export class AuthServerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
  }
}

export interface IWorkZone {
  work_zone_id: number;
  work_zone_name: string;
}

export interface ICountry {
  name: string;
  iso_code: string;
}

export interface ITimeZone {
  time_zone: string;
  utc: number;
  title: string;
  country?: string;
  group?: string;
}

export type TBookingRecurrence = "one-day booking" | "daily" | "weekly" | "monthly";
export type TRecurrencePattern =
  "every_X_days" | "every_X_weeks_on_bitdays" | "on_day_X_every_Y_months" | "on_X_bitday_every_Y_months";
export type TRecurrenceRange = "end_on_end_date" | "end_after_Z_occurrences";

export enum WeekDayFlags {
  Monday = 0b1000000,
  Tuesday = 0b0100000,
  Wednesday = 0b0010000,
  Thursday = 0b0001000,
  Friday = 0b0000100,
  Saturday = 0b0000010,
  Sunday = 0b0000001,
}

export interface ICapabilities {
  admin_group: boolean; // binary integer
  sys_admin_group: boolean;
  ops_group: boolean;
  read_only_user: boolean;
  org_id: number; // ID of organisation
  billing_entity_id: number; // ID of billing entity within     organisation
  create_user_in_org: boolean; // binary integer
  create_user_in_be: boolean; // binary integer
  create_user_in_any_org: boolean; // binary integer
  alter_org_group_permissions: boolean; // binary integer
  alter_be_group_permissions: boolean; // binary integer
  alter_any_group_permissions: boolean; // binary integer

  add_remove_users_from_be_group: boolean; // binary integer
  add_remove_users_from_org_group: boolean; // binary integer
  add_remove_users_from_any_group: boolean; // binary integer
  add_remove_group_in_any_org: boolean; // binary integer
  create_booking_in_org: boolean; // binary integer
  create_booking_in_be: boolean; // binary integer
  create_booking_in_any_org: boolean;
  override_any_session_rate: boolean;
  show_support_page: boolean;
}

export interface IItem<T1> {
  [key: string]: any;
  value: T1;
  title: string;
  checked?: boolean;
  metadata?: any;
}

export type TBookingType = "education" | "meeting" | "event";
export type TServiceType = "rc" | "sm" | "vri" | "rti" | "nt" | "il";
export type TGooglePaymentMethod = "google_benefits" | "ai_media_po" | "own_po_24_hrs" | "gcard" | "other";
export type TSignLanguageType = "asl" | "auslan" | "bsl" | "other";
export type TSignLanguageNames =
  "American Sign Language (ASL)" | "Australian Sign Language (Auslan)" | "British Sign Language (BSL)" | "Other";
export type TStandardYesNo = "NA" | "Yes" | "No";
export type TBroadcastOrRecorded = "No" | "Yes, Recorded" | "Yes, Broadcast" | "Yes, Both";
export type TLanguageMode = "ASL(American Sign Language)" | "CASE/Transliteration" | "Other";
export type TServiceTypeName =
  | "Remote Text Interpreting"
  | "Remote Captioning"
  | "Smart LEXI"
  | "Ai-Live LEXI"
  | "Smart Lexi"
  | "Ai-Live Lexi"
  | "LEXI"
  | "Ai-Live LEXI"
  | "LEXI with Topic Model"
  | "Video Remote Interpreting (VRI)"
  | "Remote Text Interpreting (RTI)"
  | "Notetaking"
  | "Google Event - Remote Captioning"
  | "Google Event - Video Relay Interpreting"
  | "Live Streaming - LEXI"
  | "LEXI Access";
export type OrgType = number | undefined;

interface ICaptionParamID {
  caption_param_id: number; // ID of caption output parameter
}

export type IBookingTypesList = IItem<TBookingType>;
export type IServiceTypesList = IItem<TServiceType>;
export type ISummaryOptionList = IItem<string>;
export type ISignLanguageList = IItem<TSignLanguageType>;
export type IGooglePaymentMethod = IItem<TGooglePaymentMethod>;

export type IServiceTypesNameList = IItem<TServiceTypeName>;

export interface IGooglePaymentPayload {
  id: string;
  text: string;
  value: number;
}

export interface IBookingRecurrence {
  pattern: TRecurrencePattern;
  range: TRecurrenceRange;

  X?: number; // variable for pattern
  Y?: number; // variable for pattern
  Z?: number; // variable for quantity of recurrences
  end_date?: string; // , date in ISO 8601 (“YYYY-MM-DD”), of the     end date for edit-recurrence. Note that this may not be the last date, but     that the last date will be at most as late as this date, in the timezone of     the booking/usr/org override priority.
  bitday?: number; // day selection in bit form
  exclusions?: string[]; // – array of string, ISO 8601 date
}

interface IParam {
  param_name: string;
  example_value: string;
  mandatory: 0 | 1;
}

export interface IAudioInputParam extends IParam {
  audio_param_id: number;
}

export interface IAudioInput {
  audio_input_id?: number;
  audio_name: string;
  audio_params?: IAudioInputParam[];
}

/**
 * Internal interface to be used as a basement for both ICaptionParamValue and ICaptionParamSource
 */
interface ICaptionParamID {
  caption_param_id: number; // ID of caption output parameter
}

/**
 * Interface to send values to API and data from getBookings
 */
export interface ICaptionParamValue extends ICaptionParamID {
  value: string;
}

/**
 * Param without value
 */
export interface ICaptionParamSource extends IParam, ICaptionParamID {}

/**
 * Full param description
 */
export interface ICaptionParam extends ICaptionParamSource, ICaptionParamValue {}

export interface ICaptionParam extends ICaptionParamSource, ICaptionParamValue {}

interface ICaptionID {
  caption_output_id: number; // ID of audio input
}

export interface ICaptionSource extends ICaptionID {
  caption_output_name: string; // name of audio input
  caption_params: ICaptionParamSource[]; // array of caption_param_object
}

export interface ICaptionValue extends ICaptionID {
  caption_output_name?: string;
  booking_caption_output_id?: number;
  caption_params: ICaptionParamValue[];
  destination_languages: string | undefined;
  machine_translation: number | undefined;
}

export interface ICaption extends Omit<ICaptionSource, "caption_params"> {
  booking_caption_output_id?: number;
  caption_params: ICaptionParam[];
}

export interface ISubServiceValue {
  sub_service: string | undefined;
}

/**
 * @deprecated
 */
export interface ICaptionOutputParam extends IParam {
  caption_param_id: number; // ID of caption output parameter
}

export interface IAudioParam {
  audio_param_id: number;
  param_name: string;
  example_value: string;
  mandatory: boolean;
  value?: string;
}

export interface IAudio {
  audio_input_id: number;
  booking_audio_input_id?: number;
  audio_name: string;
  params?: IAudioParam[];
}

export type TTheme = "ai-media" | "intel" | "light";
export type TCaptionOverrun = "Varies per session" | "Always" | "Never";
export type TServiceTypeList =
  | "Remote Captioning"
  | "Smart Lexi"
  | "Ai-Live Lexi"
  | "LEXI"
  | "Ai-Live LEXI"
  | "LEXI with Topic Model"
  | "Video Remote Interpreting (VRI)"
  | "Remote Text Interpreting (RTI)"
  | "Notetaking"
  | "LEXI Access";
export type TTranscriptionRetention =
  "" | "0 days" | "3 days" | "30 days" | "60 days" | "90 days" | "6 months" | "1 year" | "2 year" | "3 year";

export interface IOrganization {
  has_knowledge_base?: number;
  org_id?: number;
  org_name: string;
  org_caption_overrun: TCaptionOverrun;
  guaranteed_period: number;
  show_pricing_estimate: boolean;
  // classes_can_be_created: boolean;
  // meetings_can_be_created: boolean;
  // events_can_be_created: boolean;
  theme?: TTheme;
  hd?: string;
  multilingual: boolean;
  white_label: boolean | number;
  disclaimer_acv: boolean | number;
  recall_ai: boolean | number;
  recall_ai_msg: string;
  restrict_language: boolean;
  allow_transcripts: boolean;
  allow_transcript_tools: boolean;
  transcript_retention?: TTranscriptionRetention;
  // rc_allowed: boolean;
  // sm_allowed: boolean;
  // vri_allowed: boolean;
  // nt_allowed: boolean;
  // rti_captioning_allowed: boolean;
  // default_service_type?: string;
  show_banner?: number | undefined;
  banner_message?: string;
  logging_until?: string;
  logging_enabled?: boolean;
  settings?: any;
  deleted?: number;
}

export interface IOrganizationExtended {
  organization: IOrganization;
  audio_sources: number[];
  display_captions?: number[];
}

export interface IAIMLegalEntity {
  aim_legal_entity_id?: number;
  name: string;
  region_id: number;
  default_for_region: boolean;
}

export type IConfigValue = string | number | boolean | string[] | number[] | boolean[];

export interface IConfig {
  config_id: number;
  config_name: string;
  config_value: IConfigValue;
  value_type: string;
  metadata?: Record<string, unknown>;
  created_by: string;
  created_at: string;
  updated_by: string | null;
  updated_at: string | null;
}

export interface IBookingAudioInputParam {
  audio_param_id: number;
  value?: string;
}

export interface IBookingAudioInput {
  audio_name?: string;
  audio_params?: IBookingAudioInputParam[];
  audio_input_id: number;
  booking_audio_input_id?: number;
  audio_input_params: IBookingAudioInputParam[];
  source_languages?: string;
  /** Some payloads use singular; prefer {@link source_languages} when both exist. */
  source_language?: string;
  missing_audio_info?: 0 | 1;
}

export interface IBookingRunningAudioInput {
  audio_name: string;
  value?: string;
  source_languages?: string;
}
export interface IBookingRunningCaptions {
  caption_output_name: string;
  value?: string;
  destination_languages?: string;
}

export interface IBookingSession {
  session_id: number;
  /** Present when session carries account context distinct from booking-level defaults. */
  billing_entity_id?: number;
  date: number;
  start_epoch: number;
  end_epoch: number;
  processed_epoch?: number;
  cancelled_epoch: number;
  tag?: string;
  value?: string;
  destination_language?: string;
  /** Comma-separated; used with destination_language to detect translated-caption URLs. */
  source_languages?: string;
  /** Some payloads use singular; prefer {@link source_languages} when both exist. */
  source_language?: string;
  viewer_guid?: string;
  allowed_viewer?: string;
  emergency_session_link?: string;
  emergency_link_host_key?: string;
  transcripts?: ITranscript[];
  job_status?: number | undefined;
  service_type?: string;
  hide_private_viewer_dropdown?: number;
  hide_mlt?: number;
  live_booking_id?: number;
  hide_consumers_manager_view?: number;
  url?: string;
  notes?: string;
  prep_materials?: any;
  shareable_caption_link?: number | boolean;
  creator_location?: string;
  /** Set client-side for QR/link decisions: true when the booking org has hosted domain access configured. */
  has_hosted_domain?: boolean;
  background?: string;
  colour?: string;
  size?: number;
  font?: string;
  qr_code?: number;
  fontweight?: string;
  textalign?: string;
  padding?: number;
  border?: number;
  borderradius?: number;
  borderwidth?: number;
  highlightnewtext?: number;
  bordercolor?: string;
  lineheight?: string;
  opacity?: number;
  showscrollbar?: number;
  estimated_viewer_count?: number;
  /** Set on sessions-dialog table rows: 1 when MLT WhiteLabel share URL is used for this row. */
  sessions_dialog_mlt_caption_url?: number;
}

export interface IBooking {
  org_id?: number;
  live_booking_id: number;
  billing_entity_id: number; // billing entity ID of this booking
  title: string; // name of the meeting / lecture / stream
  short_title: string; // short name of the session, typically used for university lecture series
  start_epoch: number; // start date and time of the first session in     Unix epoch (broadly UTC) or from when sessions are valid for  recurring sessions
  duration_mins: number; // number of minutes this session runs for
  booking_product_sku: string; // product SKU URL, uuencode    simulated, describes the main session being provided
  booking_time_zone: string; // final time zone of booking
  attendees?: string; // , list of names of attendees
  fixed_fee_product_sku?: string; // , product SKU URL, uuencode simulated
  fixed_fee_override?: number; // , fixed fee in org_currency for each booking
  time_zone_override?: string; // if present, this time     zone will be used for this session rather than the default for the    organisation or the user, if it is set. Session time_zone overrides     all, user time_zone overrides organisation time_zone.
  rate_override: number; // hourly rate in org_currency.    This rate will be used instead of org_rate, if rate_override is   present.
  recurrence?: IBookingRecurrence;
  recurrence_description?: string;
  sessions: IBookingSession[];
  audio_input: IBookingAudioInput;
  caption_outputs: ICaptionValue[];
  consumers?: number[];
  prepMaterials?: IPrepMaterial[];
  transcripts?: ITranscript[];
  tags?: string;
  captioner_notes?: string;
  booking_billing_reference?: string;
  billing_reference_override?: string;
  billing_contact_email?: string;
  hide_private_viewer_dropdown?: string;
  hide_mlt?: number;
  hide_consumers_manager_view?: number;
  source_languages?: string;
  destination_languages?: string;
  service_type?: string;
  allowed_viewer?: string;
  allow_transcript?: number | string;
  viewerGuid?: string;
  selectedServiceType?: string;
  subServices?: string;
  sign_language_type?: string;
  language_mode?: string;
  open_access?: number;
  topic_model_language?: string;
  topic_model_language_other?: string;
  topic_model_update_existing?: number | boolean;
  topic_model_curation_notes?: string;
  viewer_guid?: string;
  interpreter_attire?: string;
  preferred_interpreters?: string;
  event_recorded_live_broadcast?: number | string;
  presenting_participant?: string;
  person_using_vri?: string;
  num_interpreters_needed?: string;
  running_audio_input?: IBookingRunningAudioInput;
  running_caption_outputs?: IBookingRunningCaptions;
  session_id?: number;
  source_language?: string;
  destination_language?: string;
  session_destination_language?: string;
  booking_user?: string;
  point_of_contact?: string;
  shareable_caption_link?: number | boolean;
  google_payment_method?: IGooglePaymentPayload[];
  google_payment_method_other_check?: boolean;
  google_payment_method_other_text?: string;
  google_viewers?: number;
  google_docs_transcript_link?: string;
  session_ids?: number[];
  estimated_viewer_count?: number;
  /** Completed bookings: `1` when feedback was already submitted (disable feedback action). */
  feedback?: number | boolean | string;
  /** LBP-352: true while the backend is still creating/processing this booking's sessions. */
  sessions_processing?: boolean;
}

export interface IFullBooking extends IBooking {
  captions?: ICaption[];
  audio?: IAudio;
}

export interface ICommonRecurrenceParams {
  pattern: TRecurrencePattern;
  bitday?: number;
  X: number;
  Y?: number;
}

export interface IBookableEntity {
  org_id: number; // ID of the organisation
  name: string; // name of the organisation
  billing_entities: {
    billing_entity_id: number;
    name: string;
  }[];
}

export type TPaymentType = "post-pay" | "upfront" | "upfront_or_post-pay";

export interface ISelectOption {
  title: string;
  value: number;
}

export interface IPrepMaterial {
  prep_material_id: number;
  id?: number;
  filename: string;
  fileData?: File;
  metadata?: any;
  new?: boolean;
  source?: "session" | "series";
}

export interface ITranscript {
  session_id: number;
  live_booking_id: number;
  transcript_id: number;
  filename: string;
  added_epoch: number;
  summary: string;
  fileData?: File;
  org_id?: number;
  billing_entity_id?: number;
  checked?: boolean;
}

export interface IAnalytics {
  id?: number;
  action_type: string;
  timestamp: string;
  user_id?: number;
  session_id?: number;
  details?: any;
}

export interface IRecurrenceCalculate {
  time_zone?: string;
  start_epoch: number;
  recurrence: IBookingRecurrence;
  destination_language?: string;
  source_language?: string;
  allow_viewer?: string;
  viewer_guid?: string;
}

export interface IMonth {
  year: number;
  month: number;
}

export interface ISessionDate2 {
  date: Date;
  excluded: boolean;
}

export interface IMonthView {
  view: IMonth;
  dates: ISessionDate2[];
}

export interface IBillingEntity {
  billing_entity_id: number; // ID of billing entity
  name: string; // name of billing_entity
  org_id: number; // ID of the organisation
  billing_contact_user_id: number;
  billing_reference: string; // default billing reference for     invoices
  currency: string; // three-character currency (AUD, USD,                                                      GBP etc)
  time_zone: string; // standard TZ database time_zone     name
  hourly_rate: number; // – float, default hourly rate for live bookings for     this billing entity
  hourly_rate_us?: number; // – float, hourly rate for US bookings
  min_duration: number;
  min_duration_us?: number;
  min_charge: number;
  min_charge_us?: number;
  cost_round: number;
  cost_round_us?: number;
  sap_db?: string; // SAP database that this     billing entity lives in
  sap_bp_code?: string; // SAP BP code     representing this billing entity
  payment_type: TPaymentType;
  trading_name?: string;
  legal_name?: string;
  business_number?: string;
  tax_number?: string;
  aim_legal_entity_id: number;
  region_id: number; // ID of region that this billing entity is located
  hide_billing_reference: number;
  source_languages?: string;
  destination_languages?: string;
  hide_billing_contact?: number;
  payment_message?: string;
  hide_consumer_dropdown?: number;
  hide_private_viewer_dropdown?: number;
  hide_mlt?: number;
  hide_consumers_manager_view?: number;
  deleted?: number;
  booking_defaults?: IBookingDefaults;
  service_types?: any;
  summary_options?: any;
  booking_creation_types?: number[];
  default_service_type?: string;
  default_summary_option?: string;
  settings?: any;
  allow_machine_translations?: number;
}

export interface IPayment {
  type: TPaymentType;
  stripe?: Stripe;
  // intent?: string;
  stripeCard?: any;
}

export type TBookingStatus = "upcoming" | "running" | "completed" | "cancelled";

export interface ICanFocus {
  focused: EventEmitter<boolean>;
}

export type TIconSize = "small" | "regular" | "large";

export enum SnackIconSet {
  error = "cancel",
  success = "check",
  info = "info",
}

export interface ISnackbarData {
  title: string;
  message: string;
  action: string;
  snack_type: SnackIconSet;
}

export type TSessionStatus =
  | "past"
  | "running"
  | "processing"
  | "cancelled"
  | "excluded"
  | "confirmed"
  | "interpreter-confirmed"
  | "captioner-confirmed";

export interface ISession {
  [key: string]: any;
  hide_private_viewer_dropdown: Boolean;
  session_id: number;
  date: number | string;
  duration: number;
  status: TSessionStatus;
  tag?: string;
  value?: string;
  destination_language?: string;
  source_languages?: string;
  service_type?: string;
  hide_mlt?: number;
  viewer_guid?: string;
  allowed_viewer?: string;
  emergency_session_link?: string;
  emergency_link_host_key?: string;
  job_status?: number | undefined;
  display_captions_type?: string;
  live_booking_id?: number;
  start_date?: number | string;
  end_epoch?: any;
  url?: string;
  notes?: string;
  prep_materials?: any;
  fromSessionDialog?: boolean;
  creator_location?: string;
  /** Set client-side for QR/link decisions: true when the booking org has hosted domain access configured. */
  has_hosted_domain?: boolean;
  background?: string;
  colour?: string;
  size?: number;
  font?: string;
  qr_code?: number;
  shareable_caption_link?: number | boolean;
  fontweight?: string;
  textalign?: string;
  padding?: number;
  border?: number;
  borderradius?: number;
  borderwidth?: number;
  highlightnewtext?: number;
  bordercolor?: string;
  lineheight?: string;
  opacity?: number;
  showscrollbar?: number;
}

export interface ISessions {
  title: string;
  time_zone: string;
  sessions: ISession[];
  exclusions?: string[];
}
